import { useMemo, useState } from "react";
import { Button } from "./ui/button";
import { parseSdiError } from "../utils/sdiErrorMessages";
import { ChevronDown, ChevronUp, AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Errore invio SDI: messaggio leggibile + espansione dettaglio tecnico.
 */
export default function SdiErrorMessage({
  error,
  userMessage,
  title = "Invio SDI non riuscito",
  compact = false,
  /** "inline" per celle tabella: solo testo + link dettaglio */
  variant = "default",
  className,
}) {
  const parsed = useMemo(
    () => parseSdiError(error, { userMessage }),
    [error, userMessage],
  );
  const [showTech, setShowTech] = useState(false);
  const hasTech = Boolean(
    parsed.technical &&
      (parsed.technical.length > (parsed.summary || "").length + 15 ||
        parsed.technical.includes("{") ||
        parsed.technical.includes("HTTP")),
  );

  if (!parsed.summary && !parsed.technical) return null;

  if (variant === "inline") {
    return (
      <div className={cn("min-w-0", className)}>
        <p className="text-xs text-red-700 leading-snug">{parsed.summary}</p>
        {hasTech && (
          <>
            <button
              type="button"
              className="text-[11px] text-red-600 underline mt-0.5 hover:text-red-800"
              onClick={() => setShowTech((v) => !v)}
            >
              {showTech ? "Nascondi dettaglio tecnico" : "Dettaglio tecnico"}
            </button>
            {showTech && (
              <pre className="mt-1 max-h-28 overflow-auto rounded border border-red-100 bg-slate-50 p-1.5 text-[10px] text-slate-600 whitespace-pre-wrap break-all font-mono">
                {parsed.technical}
              </pre>
            )}
          </>
        )}
      </div>
    );
  }

  return (
    <div
      className={cn(
        "rounded-lg border border-red-200 bg-red-50 text-sm",
        compact ? "p-2" : "p-3",
        className,
      )}
    >
      <div className="flex gap-2">
        <AlertCircle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
        <div className="min-w-0 flex-1">
          {!compact && <p className="font-medium text-red-900">{title}</p>}
          <p className={cn("text-red-800", !compact && "mt-1")}>{parsed.summary}</p>
          {parsed.lines.length > 1 && (
            <ul className="mt-2 space-y-1 text-xs text-red-700 list-disc pl-4">
              {parsed.lines.slice(1).map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          )}
          {hasTech && (
            <div className="mt-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs text-red-800 hover:text-red-900 hover:bg-red-100/80"
                onClick={() => setShowTech((v) => !v)}
              >
                {showTech ? (
                  <>
                    <ChevronUp className="w-3.5 h-3.5 mr-1" />
                    Nascondi dettaglio tecnico
                  </>
                ) : (
                  <>
                    <ChevronDown className="w-3.5 h-3.5 mr-1" />
                    Dettaglio tecnico
                  </>
                )}
              </Button>
              {showTech && (
                <pre className="mt-2 max-h-40 overflow-auto rounded border border-red-100 bg-white/80 p-2 text-[10px] leading-snug text-slate-700 whitespace-pre-wrap break-all font-mono">
                  {parsed.technical}
                </pre>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
